import useSWR from 'swr'
import useSWRInfinite from 'swr/infinite'
import {
  LibraryCollectionItem,
  LibrarySearchBrand,
  LibrarySearchCollection,
  LibrarySearchGroup,
  LibrarySearchGroupPage,
  LibrarySearchResponse,
  LibrarySearchTheme,
  LibrarySearchTopic,
} from '@digital-twins/shared-types'

import { libraryCollectionsApi } from '@/lib/api'

/** The item type of each search group's "See all" page. */
export interface LibrarySearchGroupItems {
  themes: LibrarySearchTheme
  topics: LibrarySearchTopic
  collections: LibrarySearchCollection
  brands: LibrarySearchBrand
  resources: LibraryCollectionItem
}

export const LIBRARY_SEARCH_SWR_KEY = 'library/search'
export const LIBRARY_SEARCH_GROUP_SWR_KEY = 'library/search-group'

/** The backend's `MaxLength` on `q`. */
export const LIBRARY_SEARCH_QUERY_MAX_LENGTH = 200

/** The query as sent: trimmed and capped; null for a blank one (no request). */
export function toLibrarySearchQuery(q: string): string | null {
  const trimmed = q.trim().slice(0, LIBRARY_SEARCH_QUERY_MAX_LENGTH).trim()
  return trimmed.length > 0 ? trimmed : null
}

export interface UseLibrarySearchResult {
  results: LibrarySearchResponse | null
  isLoading: boolean
  hasError: boolean
}

/**
 * The grouped Library search over `GET /library/search?q=` (five groups, up
 * to 5 each). An empty or blank query reads nothing; the last results stay
 * on screen while the next query loads. The caller debounces typing.
 */
export function useLibrarySearch(q: string): UseLibrarySearchResult {
  const query = toLibrarySearchQuery(q)
  const { data, error, isLoading } = useSWR<LibrarySearchResponse>(
    query ? [LIBRARY_SEARCH_SWR_KEY, query] : null,
    () => libraryCollectionsApi.search(query as string),
    { revalidateOnFocus: false, keepPreviousData: true, shouldRetryOnError: false },
  )
  return {
    results: query ? (data ?? null) : null,
    isLoading: Boolean(query) && isLoading,
    hasError: Boolean(query) && Boolean(error),
  }
}

export interface UseLibrarySearchGroupResult<T> {
  items: Array<T>
  isLoading: boolean
  isLoadingMore: boolean
  hasError: boolean
  hasMore: boolean
  loadMore: () => void
}

type SearchGroupPageKey = [string, string, LibrarySearchGroup, string | null]

/**
 * "See all" for one search group over
 * `GET /library/search?q=&group=&cursor=`, a page at a time
 * (`useSWRInfinite`). A null group or a blank query reads nothing.
 */
export function useLibrarySearchGroup<G extends LibrarySearchGroup>(
  q: string,
  group: G | null,
): UseLibrarySearchGroupResult<LibrarySearchGroupItems[G]> {
  const query = toLibrarySearchQuery(q)
  const { data, error, isLoading, isValidating, size, setSize } = useSWRInfinite<
    LibrarySearchGroupPage<unknown>
  >(
    (pageIndex: number, previous: LibrarySearchGroupPage<unknown> | null): SearchGroupPageKey | null => {
      if (!query || !group) {
        return null
      }
      if (pageIndex > 0 && !previous?.nextCursor) {
        return null
      }
      return [LIBRARY_SEARCH_GROUP_SWR_KEY, query, group, previous?.nextCursor ?? null]
    },
    (key: SearchGroupPageKey) =>
      libraryCollectionsApi.searchGroup(key[1], key[2], key[3] ?? undefined),
    {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
      shouldRetryOnError: false,
    },
  )

  const pages = data ?? []
  const lastPage = pages.length > 0 ? pages[pages.length - 1] : null
  const hasMore = Boolean(lastPage?.nextCursor)
  const isLoadingMore = isValidating && size > pages.length
  const isActive = Boolean(query && group)

  return {
    items: pages.flatMap((page) => page.items as Array<LibrarySearchGroupItems[G]>),
    isLoading: isActive && isLoading,
    isLoadingMore,
    hasError: isActive && Boolean(error),
    hasMore,
    loadMore: () => {
      if (hasMore && !isLoadingMore) {
        void setSize(size + 1)
      }
    },
  }
}
